// Wrapper único do Sentry — espelha o padrão do mobile
// (`mobile/lib/services/metrics_service.dart`) e do backend
// (`Program.cs`).
//
// Regras compartilhadas:
//  - DSN vazia → no-op silencioso (dev local sem dependência externa).
//    O `beforeSend` retorna null pra short-circuit completo.
//  - `release` é a tag que correlaciona com backend/mobile no painel
//    do Sentry. Formato: `faromaps-admin@<sha>` quando VITE_BUILD_SHA
//    está setado, ou `faromaps-admin@dev` em build local.
//  - `tracesSampleRate` default 0.1; pode subir pra 1.0 durante load test
//    via VITE_SENTRY_TRACES_SAMPLE_RATE.
//  - `sendDefaultPii=false` — não envia IP/headers/cookies.
//  - `setUser` é chamado de fora (auth-context) quando o usuário Firebase
//    é estabelecido, mantendo a paridade com `MetricsService.setUserIdentifier`.

import * as Sentry from '@sentry/react';

const DSN = import.meta.env.VITE_SENTRY_DSN ?? '';
const TRACES_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.1',
);
const REPLAYS_SESSION_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_REPLAYS_SAMPLE_RATE ?? '0.0',
);
const REPLAYS_ON_ERROR_SAMPLE_RATE = parseFloat(
  import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? '1.0',
);

const BUILD_SHA = import.meta.env.VITE_BUILD_SHA ?? 'dev';
const RELEASE = `faromaps-admin@${BUILD_SHA}`;

// Tracing origins — Sentry injeta cabeçalhos `sentry-trace` / `baggage`
// nas chamadas que casam, permitindo correlacionar trace com o backend
// (que já envia traces do ASP.NET Core via `Sentry.AspNetCore`).
const TRACING_TARGETS: Array<string | RegExp> = [
  /^\/api\//,
  /^\/partyhub/,
  /^\/gpshub/,
  /^\/presencehub/,
];

let initialized = false;

export function initSentry() {
  if (initialized) return;
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: import.meta.env.MODE,
    release: RELEASE,
    tracesSampleRate: TRACES_SAMPLE_RATE,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Replay só fica útil em prod e custa banda — default desligado
      // (sampleRate 0). Liga via VITE_SENTRY_REPLAYS_SAMPLE_RATE=0.1
      // em deploys onde quiser gravar sessões.
      Sentry.replayIntegration({
        maskAllText: false, // bibs/nomes são públicos no live
        blockAllMedia: true, // mas logo/avatares não vão pro replay
      }),
    ],
    tracePropagationTargets: TRACING_TARGETS,
    replaysSessionSampleRate: REPLAYS_SESSION_SAMPLE_RATE,
    replaysOnErrorSampleRate: REPLAYS_ON_ERROR_SAMPLE_RATE,
    beforeSend(event) {
      // DSN vazia = no-op silencioso. Mesmo padrão do mobile/backend.
      if (!DSN) return null;
      return event;
    },
    beforeBreadcrumb(crumb) {
      if (!DSN) return null;
      return crumb;
    },
  });
}

/// Identifica o usuário no scope (UID Firebase). Equivalente ao
/// `MetricsService.setUserIdentifier` do mobile. Sem DSN, vira no-op
/// porque o SDK descarta o evento via beforeSend.
export function setSentryUser(uid: string | null) {
  if (uid) {
    Sentry.setUser({ id: uid });
  } else {
    Sentry.setUser(null);
  }
}

/// Log estruturado que vira breadcrumb no painel + `console.debug` em
/// dev. Use em paths críticos: auth, navegação, mutations, SignalR
/// lifecycle. Mesmo helper do `MetricsService.breadcrumb` do mobile.
export function breadcrumb(message: string, category = 'app', data?: Record<string, unknown>) {
  if (import.meta.env.DEV) {
    console.debug(`[${category}] ${message}`, data ?? '');
  }
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

/// Captura exceção manual. Caller deve passar contexto (qual ação
/// falhou) pra reduzir agrupamento incorreto no painel.
export function captureError(error: unknown, context?: Record<string, unknown>) {
  Sentry.captureException(error, context ? { extra: context } : undefined);
}

/// Falha degradada que **não** joga exceção — recurso externo que não carregou,
/// resposta parcial, fallback acionado. Vira EVENTO (level=warning), não
/// breadcrumb: breadcrumb só sobe grudado numa exceção, então um sintoma que
/// nunca vira crash morreria calado — exatamente o que a invariante #5 proíbe
/// em path que o público vê. Caller é responsável por **deduplicar** antes de
/// chamar: página aberta por 200 espectadores não pode virar 200 eventos.
export function captureWarning(message: string, context?: Record<string, unknown>) {
  if (import.meta.env.DEV) console.warn(`[warn] ${message}`, context ?? '');
  Sentry.captureMessage(message, {
    level: 'warning',
    ...(context ? { extra: context } : {}),
  });
}

export const BUILD_TAG = RELEASE;
export const SENTRY_ENABLED = !!DSN;
